import type { Contact } from '@/api/types/contacts';
import { contactsApi } from '@/api/queries/contacts';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useModal } from '@/components/modal/useModal';

import { getFullName } from '../contactUtils';

// Minimal shape needed to delete — satisfied by both the generated Contact
// (list grid rows) and the date-augmented Contact (detail form).
type DeletableContact = {
  id?: string;
  profile: Pick<Contact['profile'], 'titleBefore' | 'firstName' | 'lastName' | 'titleAfter'>;
  audit?: { version?: number };
};

export function useContactDeleteActions() {
  const { openModal } = useModal();
  const deleteSingleContact = contactsApi.useDelete();
  const deleteContactsInBulk = contactsApi.useBulkDelete();

  const handleDeleteSingle = (contact: DeletableContact, onDeleted?: () => void) => {
    openModal(
      <ConfirmDialog
        title="Delete Contact"
        message={`Are you sure you want to delete "${getFullName(contact.profile)}"? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={async () => {
          await deleteSingleContact.mutateAsync({
            id: contact.id ?? '',
            etag: contact.audit?.version ? `"${contact.audit.version}"` : `"1"`,
          });
          onDeleted?.();
        }}
      />,
    );
  };

  const handleBulkDelete = (ids: string[]) => {
    openModal(
      <ConfirmDialog
        title={`Delete ${ids.length} Contact${ids.length === 1 ? '' : 's'}`}
        message={`Are you sure you want to delete ${ids.length} selected contact${ids.length === 1 ? '' : 's'}? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={async () => {
          await deleteContactsInBulk.mutate(ids);
        }}
      />,
    );
  };

  return { handleDeleteSingle, handleBulkDelete };
}
